import { Button, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Feedback } from "@material-ui/icons";
import React, { useMemo } from "react";
import {
  matchPath,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { colors } from "../../colors";
import PerformanceProFeedbackDialog from "../../components/PerformancePro/PerformanceProFeedbackDialog";
import { useUpdatePerformanceProInitialState } from "../../components/PerformancePro/usePerformanceProInitialStateHooks";
import {
  NavigationItem,
  TabNavigation,
  TabNavigationVariant,
} from "../../components/UI/TabNavigation";
import {
  Features,
  useAuthorizationScope,
} from "../../globalHooks/authorizationHooks";
import {
  useSelectedMachine,
  useUserPreferences,
  useUserPreferencesActions,
} from "../../store/GlobalContext";
import { PerformanceProRoute } from "../../utility/ProtectedRoute";
import { t } from "../../utility/TranslateUtility";
import { PerformanceProOverview } from "./PerformanceProOverview";
import { PerformanceProProduction } from "./PerformanceProProduction";
import { PerformanceProQuality } from "./PerformanceProQuality";
import { PerformanceProReport } from "./PerformanceProReport";
import PerformanceProSupplierDetail from "./PerformanceProSupplierDetail";
import { PerformanceProFdaExport } from "./PerformanceProFdaExport";
import {useFeatureFlagsHooks} from "../../components/FeatureFlag/FeatureFlagHook";
import { compactNavigationHeight } from "./PerformanceProCompactNavigation";

const useStyles = makeStyles((theme) => ({
  feedbackButton: {
    position: "fixed",
    top: theme.spacing(8) + compactNavigationHeight + theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      top: theme.spacing(6) + compactNavigationHeight + theme.spacing(3),
    },
    right: theme.spacing(2),
    zIndex: 1000,
  },
}));

export default function PerformanceProPage() {
  const classes = useStyles();
  let { path, url } = useRouteMatch();
  let location = useLocation();
  const authorizationScope = useAuthorizationScope();
  const machineId = useSelectedMachine();
  const userPreferences = useUserPreferences();
  const { updateAskForFeedback } = useUserPreferencesActions();
  const { featureFlags, awaitingResponse } = useFeatureFlagsHooks();
  
  useUpdatePerformanceProInitialState(machineId);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);

  React.useEffect(() => {
    if (userPreferences.askForFeedback === true && featureFlags?.CustomerFeedback === true) {
      setFeedbackOpen(true);
    }
  }, [userPreferences.askForFeedback, featureFlags?.CustomerFeedback]);

  const handleOpenFeedback = () => {
    setFeedbackOpen(true);
  };

  const handleCloseFeedback = () => {
    setFeedbackOpen(false);
    updateAskForFeedback(false);
  };

  const navigationItems: Array<NavigationItem> = [
    {
      link: `${url}/overview`,
      title: t("performanceProNavigationTabs.navigationItems.overview"),
      shouldRender: true,
    },
  ];

  if (
    authorizationScope.hasAuthorizationForSelectedMachine(
      Features.PerformancePro,
    )
  ) {
    navigationItems.push({
      link: `${url}/production`,
      title: t("performanceProNavigationTabs.navigationItems.production"),
      shouldRender: true,
    });
    navigationItems.push({
      link: `${url}/quality`,
      title: t("performanceProNavigationTabs.navigationItems.quality"),
      shouldRender: true,
    });
    navigationItems.push({
      link: `${url}/report`,
      title: t("performanceProNavigationTabs.navigationItems.report"),
      shouldRender: true,
    });
    // US-only nav item (hidden while machine info is loading)
    if (machineId.countryCode && machineId.countryCode === "USA") {
      navigationItems.push({
        link: `${url}/traceability-records`,
        title: t("performanceProNavigationTabs.navigationItems.fdaExport"),
        shouldRender: true,
      });
    }
  }

  const selectedNavigationItem = useMemo<number>((): number => {
    const result = navigationItems.findIndex((item) =>
      matchPath(location.pathname, {
        path: item.link,
        exact: false,
        strict: false,
      }),
    );

    return result < 0 ? 0 : result;
  }, [navigationItems, location]);

  return (
    <>
      <TabNavigation
        navigationItems={navigationItems}
        variant={TabNavigationVariant.Primary}
        selectedNavigationItem={selectedNavigationItem}
      />
      <Switch>
        <PerformanceProRoute
          path={`${path}/production`}
          component={PerformanceProProduction}
        />
        <Route
          path={`${path}/quality/supplier/:supplierName/shed/:supplierShed`}
          component={PerformanceProSupplierDetail}
        />
        <PerformanceProRoute
          path={`${path}/quality`}
          component={PerformanceProQuality}
        />
        <PerformanceProRoute
          path={`${path}/report`}
          component={PerformanceProReport}
        />

        {machineId?.countryCode === "USA" && (
          <PerformanceProRoute
            path={`${path}/traceability-records`}
            component={PerformanceProFdaExport}
          />
        )}

        <Route path={`${path}/overview`} component={PerformanceProOverview} />

        <Route path={`${path}/`} component={PerformanceProOverview} />
      </Switch>
        { !awaitingResponse && featureFlags?.CustomerFeedback &&
          <div className={classes.feedbackButton}>
            <Tooltip title={t("performanceProNavigationTabs.giveFeedback")}>
              <Button
                aria-label="open-feedback"
                onClick={handleOpenFeedback}
                style={{
                  borderRadius: "50px",
                  backgroundColor: colors.mobaBlue,
                  color: "#ffffff",
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
                startIcon={<Feedback />}
                variant="contained"
              >
                {t("performanceProNavigationTabs.feedback")}
              </Button>
            </Tooltip>
          </div>
        }
      <PerformanceProFeedbackDialog
        open={feedbackOpen}
        onClose={handleCloseFeedback}
      />
    </>
  );
}
